const CHORD_CORE_PATTERN = /(?:N\.C\.|NC|[A-G][#b]?(?:maj|min|m|sus|dim|aug|add|mmaj)?(?:\d+)?(?:\/[A-G][#b]?)?(?:\([^)]*\))?)/;
const CHORD_TOKEN_PATTERN = new RegExp(
  `(^|[\\s|([{])(${CHORD_CORE_PATTERN.source})(?=$|[\\s|\\])}:,.;!?-])`,
  "g"
);
const REPEAT_TOKEN_PATTERN = /%/g;
const PLAIN_SECTION_HEADER_PATTERN = /^(?:intro|verse(?:\s+\d+)?|pre-chorus|chorus|post-chorus|bridge|interlude|outro|refrain|tag)$/i;

const isSectionHeader = (line) => {
  const trimmedLine = line.trim();
  return /^\[[^\]]+\]$/.test(trimmedLine) || PLAIN_SECTION_HEADER_PATTERN.test(trimmedLine);
};

const isChordLine = (line) => {
  const trimmedLine = line.trim();
  if (!trimmedLine || isSectionHeader(trimmedLine)) {
    return false;
  }

  const normalizedLine = trimmedLine.replace(/\(([A-G][#b]?(?:maj|min|m|sus|dim|aug|add|mmaj)?(?:\d+)?(?:\/[A-G][#b]?)?(?:\([^)]*\))?)\)/g, "$1");
  const withoutChords = normalizedLine
    .replace(CHORD_TOKEN_PATTERN, "")
    .replace(REPEAT_TOKEN_PATTERN, "")
    .replace(/[|()[\]{}.,:'"-]/g, "")
    .replace(/\s+/g, "");
  return withoutChords.length === 0;
};

const getChordPositions = (line) => {
  const positions = [];
  const matches = line.matchAll(CHORD_TOKEN_PATTERN);

  for (const match of matches) {
    positions.push({
      chord: match[2],
      index: (match.index || 0) + match[1].length,
    });
  }

  return positions;
};

const buildBlocks = (tabContent) => {
  const lines = tabContent.split("\n");
  const blocks = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      blocks.push({ type: "spacer", id: `spacer-${index}` });
      continue;
    }

    if (isSectionHeader(trimmedLine)) {
      blocks.push({
        type: "section",
        id: `section-${index}`,
        text: trimmedLine.replace(/^\[|\]$/g, ""),
      });
      continue;
    }

    if (
      isChordLine(line) &&
      index + 1 < lines.length &&
      lines[index + 1].trim() &&
      !isChordLine(lines[index + 1]) &&
      !isSectionHeader(lines[index + 1].trim())
    ) {
      blocks.push({
        type: "pair",
        id: `pair-${index}`,
        chords: getChordPositions(line),
        lyrics: lines[index + 1],
      });
      index += 1;
      continue;
    }

    blocks.push({
      type: isChordLine(line) ? "chords" : "lyrics",
      id: `line-${index}`,
      text: line,
    });
  }

  return blocks;
};

const buildChordSegment = (segment, chordPositions) => {
  if (chordPositions.length === 0) {
    return "";
  }

  const segmentStart = segment.start;
  const segmentChordEnd = chordPositions.reduce((furthestIndex, { chord, index }) => {
    if (index < segmentStart) {
      return furthestIndex;
    }

    const chordEnd = index + chord.length;
    return chordEnd > furthestIndex ? chordEnd : furthestIndex;
  }, segmentStart);
  const segmentEnd = Math.max(segment.start + segment.width, segmentChordEnd);
  const relevantChords = chordPositions.filter(
    ({ index }) => index >= segmentStart && index < segmentEnd
  );

  if (relevantChords.length === 0) {
    return "";
  }

  let chordLine = "";
  relevantChords.forEach(({ chord, index }) => {
    const relativeIndex = Math.max(0, index - segmentStart);
    if (relativeIndex > chordLine.length) {
      chordLine = chordLine + " ".repeat(relativeIndex - chordLine.length);
    }
    chordLine = chordLine + chord;
  });

  return chordLine;
};

export {
  buildBlocks,
  buildChordSegment,
  getChordPositions,
  isChordLine,
  isSectionHeader,
};
